import React, { useState, useEffect } from 'react';
import { Paper, TableContainer, Box, Typography, CircularProgress } from '@mui/material';
import TableHeaders from './TableHeaders';
import TableListRow from './TableListRow';
import CustomPagination from '../../../components/Inventory/Pagination';
import SearchToolbar from './SearchToolbar'; // Import the new SearchToolbar component
import AdminWorkshopAPIs from '../../../utilities/api/workshop/AdminWorkshopAPIs';
import { useSelector } from 'react-redux';
import nodatafound from '../../../utilities/dummy_assets/nodatafound.jpg';

const WorkshopContent = () => {
  const user = useSelector(state => state.user.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Set items per page to 5 for demonstration
  const [searchQuery, setSearchQuery] = useState(''); // State to manage search query
  const [workshopData, setWorkshopData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState(['Ongoing', 'Upcoming']);

  const getStatus = item => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fromDate = new Date(item.from_date);
    fromDate.setHours(0, 0, 0, 0);

    const toDate = new Date(item.to_date);
    toDate.setHours(0, 0, 0, 0);

    if (fromDate > today) return 'Upcoming';
    if (fromDate <= today && toDate >= today) return 'Ongoing';
    return 'Completed';
  };

  useEffect(() => {
    // Function to fetch workshop data from API
    const fetchWorkshopData = async () => {
      try {
        setLoading(true);
        const data = await AdminWorkshopAPIs.WorkshopListGet();
        setWorkshopData(data);
      } catch (error) {
        console.error('Error fetching workshop data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshopData();
  }, [user]);

  // Reset to first page when search query or status filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatuses]);

  // Filter data based on search query and selected statuses
  const filteredData = workshopData.filter(item => {
    const status = getStatus(item);
    // If no statuses are selected, show all (matching the user's request)
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(status);
    const matchesSearch = 
      (item.title && item.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.location && item.location.toLowerCase().includes(searchQuery.toLowerCase()));
      
    return matchesStatus && matchesSearch;
  });

  // Calculate total number of pages
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current items in reverse order
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData
    .slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle workshop deletion
  const handleDeleteWorkshop = deletedWorkshopId => {
    setWorkshopData(prevWorkshopData =>
      prevWorkshopData.filter(workshop => workshop.id !== deletedWorkshopId)
    );
  };

  return (
    <Box sx={{ width: '100%', px: '16px' }}>
      {/* Add search toolbar */}
      <SearchToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
      />
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : totalItems > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '0.5px solid rgba(181, 181, 181, 1)',
            background:
              'linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(0deg, #B5B5B5, #B5B5B5)',
            boxShadow: 'none',
            maxWidth: '100%',
            borderRadius: '8px',
          }}
        >
          <TableHeaders />
          {currentItems.map((item, index) => (
            <TableListRow
              key={index}
              item={item}
              onDelete={handleDeleteWorkshop}
            />
          ))}
          <CustomPagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
          />
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}
        >
          <Box textAlign="center">
            <img
              src={nodatafound}
              alt="No data found"
              style={{ maxWidth: '100%', maxHeight: '50vh' }}
            />
            <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
              No data available
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WorkshopContent;
